import { useState, useEffect, useCallback, useRef } from "react";
import { usePermissions } from "./PermissionsContext";
import { createPortal } from "react-dom";
import { WarehouseAPI, BranchAPI } from "./warehouse";
import "./index.css";
import "./Maintenance.css";

const API_BASE = "https://api.seritonex.com";

const getOrgId = () => {
  const fromSession = sessionStorage.getItem("organizationId");
  if (fromSession && Number(fromSession) > 0) return Number(fromSession);
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (user.organization_id && Number(user.organization_id) > 0)
      return Number(user.organization_id);
  } catch {}
  return 1;
};

const authHeaders = () => {
  const token = localStorage.getItem("token") || "";
  return {
    "Content-Type": "application/json",
    accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

async function apiFetch(method, path, body = null) {
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: authHeaders(),
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch {
    return null;
  }
}

function resolveUserId(userOrId) {
  if (userOrId == null) return null;
  if (typeof userOrId === "number" && !isNaN(userOrId)) return userOrId;
  if (typeof userOrId === "string") {
    const n = Number(userOrId);
    if (!isNaN(n) && n > 0) return n;
  }
  if (typeof userOrId === "object") {
    const raw = userOrId._rawUserId ?? userOrId.user_id ?? userOrId.id;
    if (raw != null && !isNaN(Number(raw)) && Number(raw) > 0)
      return Number(raw);
  }
  return null;
}

/* ═══════════════════════════════════════════════════════
   USER API
═══════════════════════════════════════════════════════ */
const UserAPI = {
  getAll: async () => {
    const data = await apiFetch("GET", "/api/v1/amis/user/all");
    if (!Array.isArray(data)) return null;
    return data.map((u) => {
      const rawId = u.user_id ?? u.id ?? null;
      return {
        id: rawId,
        _rawUserId: rawId,
        name: [u.name].filter(Boolean).join(" ") || u.fullname || "",
        surname: u.surname || "",
        email_address: u.email_address || u.email_address || "",
        group: u.group || "",
        branch: u.branch || "",
        status: u.is_active ? "Active" : "Inactive",
      };
    });
  },

  getById: async (id) => {
    const numId = resolveUserId(id);
    if (!numId) return null;
    const u = await apiFetch("POST", "/api/v1/amis/user/by-id", {
      user_id: String(numId),
    });
    if (!u) return null;
    const rawId = u.user_id ?? u.id ?? numId;
    return {
      id: rawId,
      _rawUserId: rawId,
      name: [u.name].filter(Boolean).join(" ") || u.fullname || "",
      surname: u.surname || "",
      email_address: u.email_address || u.email_address || "",
      contact_number: u.contact_number || "",
      group: u.group || "",
      branch: u.branch || "",
      status: u.is_active ? "Active" : "Inactive",
      _raw: u,
    };
  },

  create: (d) => apiFetch("POST", "/api/v1/amis/user/create", d),

  update: (id, d) => {
    const numId = resolveUserId(id);
    return apiFetch("PUT", "/api/v1/amis/user/update", {
      user_id: numId,
      ...d,
    });
  },

  updatePassword: (id, secret) => {
    const numId = resolveUserId(id);
    return apiFetch("PUT", "/api/v1/amis/user/update-password", {
      user_id: numId,
      secret,
    });
  },

  activate: (id) => {
    const numId = resolveUserId(id);
    return apiFetch("POST", "/api/v1/amis/user/activate", { user_id: numId });
  },

  deactivate: (id) => {
    const numId = resolveUserId(id);
    return apiFetch("POST", "/api/v1/amis/user/deactivate", { user_id: numId });
  },

  delete: (id) => {
    const numId = resolveUserId(id);
    return apiFetch("DELETE", "/api/v1/amis/user/delete", {
      user_id: String(numId),
    });
  },

  updatePerms: (id, d) => {
    const numId = resolveUserId(id);
    return apiFetch("POST", "/api/v1/amis/user/permissions", {
      user_id: numId,
      permissions: d,
    });
  },

  grantUserPerms: (id, perms) => {
    const numId = resolveUserId(id);
    return apiFetch("POST", "/api/v1/amis/permission/grant", {
      user_id: numId,
      permissions: perms,
      allowed: true,
    });
  },

  revokeUserPerms: (id, perms) => {
    const numId = resolveUserId(id);
    return apiFetch("POST", "/api/v1/amis/permission/revoke", {
      user_id: numId,
      permissions: perms,
      allowed: false,
    });
  },
};

/* ═══════════════════════════════════════════════════════
   GROUPS API
═══════════════════════════════════════════════════════ */
const GroupAPI = {
  getAll: () => apiFetch("GET", "/api/v1/amis/group/list"),
  getById: (groupId) =>
    apiFetch("POST", "/api/v1/amis/group/by-id", { group_id: groupId }),
  create: (name, description, organizationId) =>
    apiFetch("POST", "/api/v1/amis/group/create", {
      name,
      description,
      organization_id: organizationId ?? getOrgId(),
    }),
  update: (groupId, name, description, organizationId) =>
    apiFetch("PUT", "/api/v1/amis/group/update", {
      group_id: groupId,
      name,
      description,
      organization_id: organizationId ?? getOrgId(),
    }),
  delete: (groupId) =>
    apiFetch("DELETE", "/api/v1/amis/group/delete", { group_id: groupId }),
  getBoundUsers: (groupId) =>
    apiFetch("POST", "/api/v1/amis/group/bound-users", { group_id: groupId }),
  getUnboundUsers: (group_id) =>
    apiFetch("POST", "/api/v1/amis/group/unbound-users", { group_id }),
  bindUser: (userId, groupId) =>
    apiFetch("POST", "/api/v1/amis/group/bind-user", {
      user_id: userId,
      group_id: groupId,
    }),
  unbindUser: (userId, groupId) =>
    apiFetch("POST", "/api/v1/amis/group/unbind-user", {
      user_id: userId,
      group_id: groupId,
    }),
  getBoundPermissions: (groupId) =>
    apiFetch("POST", "/api/v1/amis/group/bound-permissions", {
      group_id: groupId,
    }),
  getUnboundPermissions: (groupId) =>
    apiFetch("POST", "/api/v1/amis/group/unbound-permissions", {
      group_id: groupId,
    }),
  bindPermission: (groupId, permissionId, allowed) =>
    apiFetch("POST", "/api/v1/amis/group/bind-permission", {
      group_id: Number(groupId),
      permission_id: Number(permissionId),
      allowed: allowed === true || allowed === 1 || allowed === "true",
    }),
  unbindPermission: (PermissionId) =>
    apiFetch("POST", "/api/v1/amis/group/unbind-permission", {
      group_permission_id: PermissionId,
    }),
  updatePermission: (PermissionId, allowed) =>
    apiFetch("POST", "/api/v1/amis/group/update-group-permission", {
      permission_id: PermissionId,
      allowed: allowed === true || allowed === 1 || allowed === "true",
    }),
  getGroupsByUser: (userId) =>
    apiFetch("POST", "/api/v1/amis/group/by-user", { user_id: userId }),
  getUnboundGroupsByUser: async (userId) => {
    const [allRes, boundRes] = await Promise.all([
      apiFetch("GET", "/api/v1/amis/group/list"),
      apiFetch("POST", "/api/v1/amis/group/by-user", { user_id: userId }),
    ]);
    const allArr = Array.isArray(allRes)
      ? allRes
      : allRes?.groups || allRes?.data || [];
    const boundArr = Array.isArray(boundRes)
      ? boundRes
      : boundRes?.groups || boundRes?.data || (boundRes ? [boundRes] : []);
    const boundIds = new Set(boundArr.map((g) => String(g.group_id ?? g.id)));
    return {
      bound: boundArr,
      unbound: allArr.filter((g) => !boundIds.has(String(g.group_id ?? g.id))),
    };
  },
};

/* ═══════════════════════════════════════════════════════
   PERMISSIONS API
═══════════════════════════════════════════════════════ */
const PermissionAPI = {
  getAll: () => apiFetch("GET", "/api/v1/amis/permission/all"),

  getById: (permissionId) =>
    apiFetch("POST", "/api/v1/amis/permission/by-id", {
      permission_id: permissionId,
    }),

  getBoundByUser: async (userId) => {
    const res = await apiFetch("POST", "/api/v1/amis/permission/by-user", {
      user_id: userId,
    });
    return res;
  },

  getUnboundByUser: async (userId) => {
    const res = await apiFetch(
      "POST",
      "/api/v1/amis/permission/unbound-by-user",
      { user_id: userId },
    );
    return res;
  },

  grant: (userId, permissionId, allowed = true) =>
    apiFetch("POST", "/api/v1/amis/permission/grant", {
      user_id: Number(userId),
      permission_id: Number(permissionId),
      allowed,
    }),

  revoke: (userPermissionId) =>
    apiFetch("POST", "/api/v1/amis/permission/revoke", {
      user_permission_id: Number(userPermissionId),
    }),

  updateUserPermission: (userPermissionId, allowed) =>
    apiFetch("POST", "/api/v1/amis/permission/update-user-permission", {
      user_permission_id: Number(userPermissionId),
      allowed,
    }),

  getByUser: (userId) =>
    apiFetch("POST", "/api/v1/amis/permission/by-user", {
      user_id: userId,
    }).then((res) => {
      const arr = Array.isArray(res)
        ? res
        : res?.permissions || res?.data || [];
      if (!Array.isArray(arr)) return {};
      const out = {};
      arr.forEach((p) => {
        const code = p?.code || p?.permission_code || p?.permission;
        const allowed =
          p?.allowed === 1 || p?.allowed === true || p?.allowed === "1";
        if (code) out[String(code)] = !!allowed;
      });
      return out;
    }),

  getBygroup: (groupId) =>
    apiFetch("POST", "/api/v1/amis/group/permissions", { group_id: groupId }),
};

/* ═══════════════════════════════════════════════════════
   PRIMITIVES
═══════════════════════════════════════════════════════ */
function Badge({ children, color = "green" }) {
  return <span className={`badge badge-${color}`}>{children}</span>;
}

function Btn({
  children,
  variant = "p",
  sm,
  onClick,
  disabled,
  type = "button",
  fullWidth,
}) {
  return (
    <button
      type={type}
      className={`btn btn-${variant}${sm ? " btn-sm" : ""}${fullWidth ? " btn-full" : ""}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

function FG({ label, children, hint }) {
  return (
    <div className="fg">
      <div className="fl">{label}</div>
      {children}
      {hint && <div className="tc3 fs10 mt-4">{hint}</div>}
    </div>
  );
}

function Inp({ value, onChange, placeholder, type = "text", disabled }) {
  return (
    <input
      className="fi"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      type={type}
      disabled={disabled}
    />
  );
}

function Sel({ value, onChange, options, disabled, multiple, size }) {
  return (
    <select
      className="fsel"
      value={multiple ? value || [] : value || ""}
      onChange={(e) => {
        if (multiple) {
          const vals = Array.from(e.target.selectedOptions).map((o) => o.value);
          onChange(vals);
        } else {
          onChange(e.target.value);
        }
      }}
      disabled={disabled}
      multiple={multiple}
      size={multiple && size ? size : undefined}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/* ─── READ-ONLY BANNER ──────────────────────────────────
   Shown inside panels when the current user can view
   but not modify the section.
──────────────────────────────────────────────────────── */
function ReadOnlyBanner({
  message = "You have view-only access to this section.",
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 14,
        background: "rgba(255,171,0,0.08)",
        border: "1px solid rgba(255,171,0,0.25)",
        borderRadius: 8,
        padding: "9px 14px",
        fontSize: 12,
        fontWeight: 500,
        color: "var(--warn)",
      }}
    >
      <span style={{ fontSize: 14 }}>🔒</span>
      <span>{message}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   MULTI-SELECT DROPDOWN
═══════════════════════════════════════════════════════ */
function MultiSelect({
  value = [],
  onChange,
  options = [],
  placeholder = "Select…",
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);
  const dropRef = useRef(null);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const realOpts = options.filter((o) => o.value !== "");
  const filtered = realOpts.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase()),
  );

  const toggle = (val) => {
    const next = value.includes(val)
      ? value.filter((v) => v !== val)
      : [...value, val];
    onChange(next);
  };

  const selectedLabels = realOpts
    .filter((o) => value.includes(o.value))
    .map((o) => ({ value: o.value, label: o.label }));

  const handleOpen = () => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (rect) {
      setDropPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
    setOpen((o) => !o);
    setSearch("");
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        !wrapRef.current?.contains(e.target) &&
        !dropRef.current?.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const dropdown = open ? (
    <div
      ref={dropRef}
      style={{
        position: "absolute",
        top: dropPos.top,
        left: dropPos.left,
        width: dropPos.width,
        zIndex: 9999,
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 8,
        boxShadow: "0 8px 28px rgba(0,0,0,0.22)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "8px 10px",
          borderBottom: "1px solid var(--border)",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          style={{ color: "var(--text3)", flexShrink: 0 }}
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search…"
          style={{
            flex: 1,
            border: "none",
            outline: "none",
            background: "transparent",
            fontSize: 12,
            color: "var(--text1)",
          }}
        />
      </div>
      <div style={{ maxHeight: 210, overflowY: "auto" }}>
        {filtered.length === 0 ? (
          <div
            style={{
              padding: "10px 12px",
              fontSize: 12,
              color: "var(--text3)",
            }}
          >
            No options found
          </div>
        ) : (
          filtered.map((o) => {
            const checked = value.includes(o.value);
            return (
              <div
                key={o.value}
                onClick={() => toggle(o.value)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  padding: "8px 12px",
                  cursor: "pointer",
                  fontSize: 13,
                  color: "var(--text1)",
                  background: checked ? "rgba(59,130,246,0.07)" : "transparent",
                  transition: "background 0.1s",
                }}
                onMouseEnter={(e) => {
                  if (!checked)
                    e.currentTarget.style.background = "var(--hover)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = checked
                    ? "rgba(59,130,246,0.07)"
                    : "transparent";
                }}
              >
                <div
                  style={{
                    width: 15,
                    height: 15,
                    borderRadius: 4,
                    flexShrink: 0,
                    border: checked ? "none" : "1.5px solid var(--border)",
                    background: checked ? "var(--blue)" : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.12s",
                  }}
                >
                  {checked && (
                    <svg width="9" height="9" viewBox="0 0 10 8" fill="none">
                      <path
                        d="M1 4l3 3 5-6"
                        stroke="#fff"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <span style={{ flex: 1, lineHeight: 1.35 }}>{o.label}</span>
              </div>
            );
          })
        )}
      </div>
      {realOpts.length > 1 && (
        <div
          style={{
            padding: "6px 12px",
            borderTop: "1px solid var(--border)",
            display: "flex",
            gap: 14,
          }}
        >
          <span
            onClick={() => onChange(realOpts.map((o) => o.value))}
            style={{
              fontSize: 11,
              color: "var(--blue)",
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            Select all
          </span>
          <span
            onClick={() => onChange([])}
            style={{ fontSize: 11, color: "var(--text3)", cursor: "pointer" }}
          >
            Clear
          </span>
          <span
            style={{ marginLeft: "auto", fontSize: 11, color: "var(--text3)" }}
          >
            {value.length} selected
          </span>
        </div>
      )}
    </div>
  ) : null;

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <div
        onClick={handleOpen}
        className="fi"
        style={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 4,
          minHeight: 38,
          padding: selectedLabels.length > 0 ? "5px 10px 5px 8px" : undefined,
          userSelect: "none",
        }}
      >
        {selectedLabels.length === 0 ? (
          <span style={{ color: "var(--text3)", fontSize: 13 }}>
            {placeholder}
          </span>
        ) : (
          selectedLabels.map((item) => (
            <span
              key={item.value}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                background: "rgba(59,130,246,0.1)",
                color: "var(--blue)",
                borderRadius: 5,
                padding: "2px 7px",
                fontSize: 12,
                fontWeight: 500,
                lineHeight: 1.5,
              }}
            >
              {item.label}
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(item.value);
                }}
                style={{
                  cursor: "pointer",
                  opacity: 0.55,
                  fontSize: 14,
                  lineHeight: 1,
                  marginLeft: 1,
                }}
              >
                ×
              </span>
            </span>
          ))
        )}
        <span
          style={{
            marginLeft: "auto",
            color: "var(--text3)",
            fontSize: 10,
            flexShrink: 0,
            transition: "transform 0.15s",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          ▼
        </span>
      </div>
      {mounted && dropdown && createPortal(dropdown, document.body)}
    </div>
  );
}

function SearchBar({ value, onChange, placeholder }) {
  return (
    <div className="tsearch">
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
      >
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35" />
      </svg>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function ActionMenu({ items }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [mounted, setMounted] = useState(false);
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!open) return;
      if (
        wrapperRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      )
        return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleToggle = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      const menuWidth = 170;
      const left = Math.min(
        Math.max(rect.left, 8),
        window.innerWidth - menuWidth - 8,
      );
      setPos({ top: rect.bottom + 8, left });
    }
    setOpen((prev) => !prev);
  }, []);

  const menu = open ? (
    <div
      className="adrop"
      ref={menuRef}
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        right: "auto",
        zIndex: 9999,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {items.map((item, i) => (
        <div
          key={i}
          className={`adrop-item${item.danger ? " red" : ""}`}
          onClick={() => {
            setOpen(false);
            item.onClick?.();
          }}
        >
          {item.label}
        </div>
      ))}
    </div>
  ) : null;

  return (
    <div className="action-wrap" ref={wrapperRef}>
      <button
        ref={buttonRef}
        type="button"
        className="btn btn-g btn-sm"
        onClick={handleToggle}
      >
        •••
      </button>
      {mounted && menu && createPortal(menu, document.body)}
    </div>
  );
}

function Toast({ toasts }) {
  return (
    <div className="toast-wrap">
      {toasts.map((t) => (
        <div key={t.id} className={`toast toast-${t.type}`}>
          {t.msg}
        </div>
      ))}
    </div>
  );
}

function ReviewBlock({ data }) {
  return (
    <div className="review-block">
      {Object.entries(data)
        .filter(([, v]) => v)
        .map(([k, v]) => (
          <div key={k} className="review-row">
            <span className="review-label">{k}</span>
            <span className="review-value">{String(v)}</span>
          </div>
        ))}
    </div>
  );
}

function FormCard({ icon, label, children }) {
  return (
    <div className="panel mb-16">
      <div className="ph">
        {icon && <span className="fs-16">{icon}</span>}
        <span className="ph-title">{label}</span>
      </div>
      <div className="pb">{children}</div>
    </div>
  );
}

function FormFooter({
  onCancel,
  onSubmit,
  loading,
  label = "Save",
  danger,
  hideCancel,
}) {
  return (
    <div className="fb gap-8 pt-16">
      {!hideCancel && (
        <Btn variant="g" onClick={onCancel} disabled={loading}>
          Cancel
        </Btn>
      )}
      <Btn variant={danger ? "d" : "p"} onClick={onSubmit} disabled={loading}>
        {loading ? <span className="spin-icon" /> : label}
      </Btn>
    </div>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="overlay" onClick={onClose}>
      <div
        className={`modal${wide ? " modal-lg" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mhdr">
          <span className="mtitle">{title}</span>
          <Btn variant="g" sm onClick={onClose}>
            ✕
          </Btn>
        </div>
        <div className="mbody">{children}</div>
      </div>
    </div>
  );
}

function DataTable({ columns, rows, emptyMsg = "No data found" }) {
  return (
    <div className="tbl-wrap">
      <table className="tbl">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="empty">
                {emptyMsg}
              </td>
            </tr>
          ) : (
            rows
          )}
        </tbody>
      </table>
    </div>
  );
}

function SaveBanner({ status }) {
  if (!status) return null;
  const isOk = status === "saved";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: isOk ? "rgba(0,230,118,0.10)" : "rgba(255,23,68,0.10)",
        border: `1px solid ${isOk ? "rgba(0,230,118,0.35)" : "rgba(255,23,68,0.35)"}`,
        borderRadius: 8,
        padding: "10px 14px",
        fontSize: 12,
        fontWeight: 600,
        color: isOk ? "var(--success)" : "var(--danger)",
        animation: "toast-enter 0.2s ease",
      }}
    >
      <span style={{ fontSize: 14 }}>{isOk ? "✓" : "✗"}</span>
      <span>
        {isOk
          ? "Changes saved successfully"
          : "Failed to save — please try again"}
      </span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   ACTION COLOR HELPER
═══════════════════════════════════════════════════════ */
function actionColor(action) {
  switch ((action || "").toUpperCase()) {
    case "CREATE":
      return { bg: "rgba(0,230,118,0.12)", fg: "var(--success)" };
    case "UPDATE":
      return { bg: "rgba(64,196,255,0.12)", fg: "var(--blue)" };
    case "DELETE":
      return { bg: "rgba(255,23,68,0.12)", fg: "var(--danger)" };
    case "READ":
      return { bg: "rgba(255,171,0,0.12)", fg: "var(--warn)" };
    default:
      return { bg: "var(--surface)", fg: "var(--text3)" };
  }
}

/* ═══════════════════════════════════════════════════════
   EDIT USER — UNIFIED 5-TAB PAGE
═══════════════════════════════════════════════════════ */
const ALL_EDIT_USER_TABS = [
  { id: "details", label: "Details" },
  { id: "groups", label: "Groups" },
  { id: "permissions", label: "Permissions" },
  { id: "branches", label: "Branches" },
  { id: "warehouses", label: "Warehouses" },
];

export function EditUserPage({
  user,
  branches,
  warehouses = [],
  onSave,
  onBack,
}) {
  const [activeTab, setActiveTab] = useState("details");
  const [createdDate, setCreatedDate] = useState(null);
  const [orgName, setOrgName] = useState(null);

  // ── Permission gates ──────────────────────────────────
  const {
    canViewGroups,
    canUpdateUserGroup,
    canViewPermissions,
    canUpdatePermissions,
  } = usePermissions();

  // Filter tabs to only those the user is allowed to see
  const visibleTabs = ALL_EDIT_USER_TABS.filter((t) => {
    if (t.id === "groups") return canViewGroups || canUpdateUserGroup;
    if (t.id === "permissions")
      return canViewPermissions || canUpdatePermissions;
    return true;
  });

  // If the active tab was filtered out, fall back to details
  const safeTab = visibleTabs.find((t) => t.id === activeTab)
    ? activeTab
    : "details";

  const fmtDate = (s) => {
    if (!s) return null;
    try {
      const iso = String(s).trim().replace(" ", "T");
      const d = new Date(iso);
      if (Number.isFinite(d.getTime())) return d.toLocaleDateString();
      return String(s).split(" ")[0];
    } catch {
      return String(s).split(" ")[0];
    }
  };

  useEffect(() => {
    let mounted = true;
    const uid = resolveUserId(user);
    if (!uid) return;
    UserAPI.getById(uid).then((fresh) => {
      if (!mounted || !fresh) return;
      const raw = fresh._raw || fresh || {};
      const candidates = [
        raw.date_created,
        raw.date_created_at,
        raw.dateCreated,
        raw.created_at,
        raw.created,
        raw.createdAt,
        raw.date_registered,
      ];
      const found = candidates.find(
        (c) => c != null && String(c).trim() !== "",
      );
      if (found) setCreatedDate(found);
      const org =
        raw.organization?.name ||
        raw.organization_name ||
        raw.Organization ||
        fresh.organization ||
        null;
      if (org) setOrgName(org);
    });
    return () => {
      mounted = false;
    };
  }, [user]);

  return (
    <div className="page action-page" style={{ maxWidth: 1600 }}>
      <div className="mb-22">
        <div className="action-page-header" style={{ marginTop: 24 }}>
          <p className="action-page-subtitle">Edit User Account</p>
          <div className="action-page-subject">{user.name}</div>
          <div className="action-page-info">
            {(() => {
              const first = user.name || "—";
              const last = user.surname || "—";
              const email = user.email || user.email_address || "—";
              const org = orgName || user.organization_name || "—";
              return (
                <>
                  <div className="info-col">
                    <div className="info-item">
                      <strong>First name:</strong> {first}
                    </div>
                    <div className="info-item">
                      <strong>Surname:</strong> {last}
                    </div>
                  </div>
                  <div className="info-col">
                    <div className="info-item">
                      <strong>Organization:</strong> {org}
                    </div>
                    <div className="info-item">
                      <strong>Email:</strong> {email}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
          <div className="action-page-created" style={{ maxWidth: 1600 }}>
            Created: {createdDate ? fmtDate(createdDate) : "—"}
          </div>
        </div>
      </div>

      {/* Only render tabs that the user is allowed to see */}
      <div className="eu-tabs">
        {visibleTabs.map((t) => (
          <button
            key={t.id}
            className={`eu-tab${safeTab === t.id ? " eu-tab--active" : ""}`}
            onClick={() => setActiveTab(t.id)}
          >
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {safeTab === "details" && (
        <EditDetailsPanel user={user} onSave={onSave} />
      )}
      {safeTab === "groups" && (canViewGroups || canUpdateUserGroup) && (
        <EditGroupsPanel user={user} />
      )}
      {safeTab === "permissions" &&
        (canViewPermissions || canUpdatePermissions) && (
          <EditPermissionsPanel user={user} />
        )}
      {safeTab === "branches" && (
        <EditBranchesPanel user={user} allBranches={branches} />
      )}
      {safeTab === "warehouses" && <EditWarehousesPanel user={user} />}

      <div className="fb" style={{ marginBottom: 40 }}>
        <Btn variant="g" onClick={onBack}>
          Back
        </Btn>
      </div>
    </div>
  );
}

/* ─── DETAILS PANEL ─────────────────────────────────────── */
function EditDetailsPanel({ user, onSave }) {
  const { canUpdateUsers } = usePermissions();

  const [d, setD] = useState({
    fname: user.name || "",
    contact_number: user.contact_number || "",
    lname: user.surname || "",
    email_address: user.email_address || "",
    status: user.status || "Active",
  });
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const upd = (k, v) => setD((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    let mounted = true;
    const uid = resolveUserId(user);
    if (!uid) return;
    UserAPI.getById(uid).then((fresh) => {
      if (mounted && fresh)
        setD({
          fname: fresh.name || "",
          contact_number: fresh.contact_number || "",
          lname: fresh.surname || "",
          email_address: fresh.email_address || "",
          status: fresh.status || "Active",
        });
    });
    return () => {
      mounted = false;
    };
  }, [user.id]);

  async function submit() {
    setLoading(true);
    setSaveStatus(null);
    const uid = resolveUserId(user);
    const payload = {
      name: d.fname.trim(),
      surname: (d.lname || "").trim(),
      email_address: d.email_address,
      contact_number: d.contact_number,
      status: d.status,
      is_active: d.status === "Active",
    };
    const res = await UserAPI.update(uid, payload);
    setSaveStatus(res !== null ? "saved" : "error");
    if (res !== null && onSave) onSave({ ...user, ...payload });
    setLoading(false);
    setTimeout(() => setSaveStatus(null), 3500);
  }

  return (
    <div>
      {!canUpdateUsers && (
        <ReadOnlyBanner message="You have view-only access to user details." />
      )}

      <FormCard label="Personal Information">
        <div className="g2">
          <FG label="First Name">
            <Inp
              value={d.fname}
              onChange={(v) => upd("fname", v)}
              disabled={!canUpdateUsers}
            />
          </FG>
          <FG label="Phone Number">
            <Inp
              value={d.contact_number || ""}
              onChange={(v) => upd("contact_number", v)}
              placeholder="Phone number"
              disabled={!canUpdateUsers}
            />
          </FG>
          <FG label="Last Name">
            <Inp
              value={d.lname}
              onChange={(v) => upd("lname", v)}
              disabled={!canUpdateUsers}
            />
          </FG>
        </div>
        <FG label="Email Address">
          <Inp
            value={d.email_address}
            onChange={(v) => upd("email_address", v)}
            type="email"
            disabled={!canUpdateUsers}
          />
        </FG>
        <FG label="Account Status">
          <Sel
            value={d.status}
            onChange={(v) => upd("status", v)}
            options={[
              { value: "Active", label: "Active" },
              { value: "Inactive", label: "Inactive" },
            ]}
            disabled={!canUpdateUsers}
          />
        </FG>
      </FormCard>

      <SaveBanner status={saveStatus} />

      {canUpdateUsers && (
        <div className="fb gap-8 pt-16" style={{ justifyContent: "flex-end" }}>
          <Btn variant="p" onClick={submit} disabled={loading}>
            {loading ? <span className="spin-icon" /> : "Save Details"}
          </Btn>
        </div>
      )}
    </div>
  );
}

/* ─── GROUPS PANEL ──────────────────────────────────────── */
function EditGroupsPanel({ user }) {
  const { canUpdateUserGroup } = usePermissions();

  const uid = resolveUserId(user);
  const [boundGroups, setBoundGroups] = useState([]);
  const [unboundGroups, setUnboundGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const { bound, unbound } = await GroupAPI.getUnboundGroupsByUser(uid);
    setBoundGroups(bound);
    setUnboundGroups(unbound);
    setLoading(false);
  }, [uid]);

  useEffect(() => {
    load();
  }, [load]);

  function flash(ok) {
    setSaveStatus(ok ? "saved" : "error");
    setTimeout(() => setSaveStatus(null), 3000);
  }

  async function bindGroup(gid) {
    if (!canUpdateUserGroup) return;
    setSaving(gid);
    const res = await GroupAPI.bindUser(uid, gid);
    flash(res !== null);
    await load();
    setSaving(null);
  }

  async function unbindGroup(gid) {
    if (!canUpdateUserGroup) return;
    setSaving(gid);
    const res = await GroupAPI.unbindUser(uid, gid);
    flash(res !== null);
    await load();
    setSaving(null);
  }

  const matchSearch = (g) =>
    `${g.name || ""} ${g.description || ""}`
      .toLowerCase()
      .includes(search.toLowerCase());

  if (loading)
    return (
      <div className="empty" style={{ padding: "32px 0" }}>
        Loading groups…
      </div>
    );

  return (
    <div>
      {!canUpdateUserGroup && (
        <ReadOnlyBanner message="You have view-only access to group assignments." />
      )}

      <div style={{ marginBottom: 12 }}>
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search groups…"
        />
      </div>
      <SaveBanner status={saveStatus} />
      <div className="g2" style={{ alignItems: "start", marginTop: 12 }}>
        {canUpdateUserGroup && (
          <FormCard label={`Available Groups (${unboundGroups.length})`}>
            {unboundGroups.filter(matchSearch).length === 0 ? (
              <div className="empty">All groups already assigned.</div>
            ) : (
              <DataTable
                columns={["Name", "Description", "Action"]}
                rows={unboundGroups.filter(matchSearch).map((g) => {
                  const gid = g.group_id ?? g.id;
                  return (
                    <tr key={gid}>
                      <td className="fw-6">{g.name || g.group_name || "-"}</td>
                      <td className="tc3" style={{ fontSize: 11 }}>
                        {g.description || "-"}
                      </td>
                      <td>
                        <Btn
                          variant="p"
                          sm
                          onClick={() => bindGroup(gid)}
                          disabled={saving === gid}
                        >
                          {saving === gid ? (
                            <span className="spin-icon" />
                          ) : (
                            "+ Add"
                          )}
                        </Btn>
                      </td>
                    </tr>
                  );
                })}
              />
            )}
          </FormCard>
        )}

        <FormCard label={`Assigned Groups (${boundGroups.length})`}>
          {boundGroups.filter(matchSearch).length === 0 ? (
            <div className="empty">Not in any groups yet.</div>
          ) : (
            <DataTable
              columns={
                canUpdateUserGroup
                  ? ["Name", "Description", "Action"]
                  : ["Name", "Description"]
              }
              rows={boundGroups.filter(matchSearch).map((g) => {
                const gid = g.group_id ?? g.id;
                return (
                  <tr key={gid}>
                    <td className="fw-6">{g.name || g.group_name || "-"}</td>
                    <td className="tc3" style={{ fontSize: 11 }}>
                      {g.description || "-"}
                    </td>
                    {canUpdateUserGroup && (
                      <td>
                        <Btn
                          variant="d"
                          sm
                          onClick={() => unbindGroup(gid)}
                          disabled={saving === gid}
                        >
                          {saving === gid ? (
                            <span className="spin-icon" />
                          ) : (
                            "Remove"
                          )}
                        </Btn>
                      </td>
                    )}
                  </tr>
                );
              })}
            />
          )}
        </FormCard>
      </div>
    </div>
  );
}

/* ─── PERMISSIONS PANEL ──────────────────────────────────── */

function EditPermissionsPanel({ user }) {
  const { canUpdatePermissions } = usePermissions();
  const uid = resolveUserId(user);

  const [allPerms, setAllPerms] = useState([]);
  const [boundMap, setBoundMap] = useState(new Map()); // code -> { user_permission_id, allowed } — EXPLICIT overrides only
  const [loading, setLoading] = useState(true);
  const [savingCode, setSavingCode] = useState(null);
  const [savingModule, setSavingModule] = useState(null);
  const [search, setSearch] = useState("");
  const [flashMsg, setFlashMsg] = useState(null);
  const [collapsed, setCollapsed] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const [allRes, boundRes] = await Promise.all([
      apiFetch("GET", "/api/v1/amis/permission/all"),
      PermissionAPI.getBoundByUser(uid),
    ]);

    const perms = Array.isArray(allRes)
      ? allRes
      : allRes?.permissions || allRes?.data || [];
    setAllPerms(perms);

    const boundArr = Array.isArray(boundRes)
      ? boundRes
      : boundRes?.permissions || boundRes?.data || [];
    const bMap = new Map();
    for (const p of boundArr) {
      if (!p.code) continue;
      bMap.set(p.code, {
        user_permission_id: p.user_permission_id ?? p.id,
        allowed: p.allowed === 1 || p.allowed === true || p.allowed === "1",
      });
    }
    setBoundMap(bMap);
    setLoading(false);
  }, [uid]);

  useEffect(() => {
    load();
  }, [load]);

  function flash(ok, text) {
    setFlashMsg({ type: ok ? "success" : "danger", text });
    setTimeout(() => setFlashMsg(null), 3000);
  }

  function toggleCollapse(mod) {
    setCollapsed((prev) => ({ ...prev, [mod]: !prev[mod] }));
  }

  // ── Core action: called the moment a radio is clicked ──
  async function handleSetAllowed(p, allowed) {
    if (!canUpdatePermissions) return;
    const code = p.code;
    const pid = p.permission_id ?? p.id;

    // Effective current state: an explicit record wins, otherwise
    // the permission is treated as Allowed by default.
    const existing = boundMap.get(code);
    const currentEffective = existing ? existing.allowed : true;
    if (currentEffective === allowed) return; // already in that state

    setSavingCode(code);
    const res = await PermissionAPI.grant(uid, pid, allowed);

    if (res !== null) {
      setBoundMap((prev) => {
        const next = new Map(prev);
        next.set(code, {
          user_permission_id:
            res?.user_permission_id ?? res?.id ?? existing?.user_permission_id,
          allowed,
        });
        return next;
      });
      flash(
        true,
        `${p.name || p.code} set to ${allowed ? "Allowed" : "Denied"}`,
      );
    } else {
      flash(false, `Failed to update ${p.name || p.code}`);
    }
    setSavingCode(null);
  }

  async function handleSetAllowedForModule(mod, perms, allowed) {
    if (!canUpdatePermissions) return;
    setSavingModule(mod);
    for (const p of perms) {
      const pid = p.permission_id ?? p.id;
      const existing = boundMap.get(p.code);
      const currentEffective = existing ? existing.allowed : true;
      if (currentEffective === allowed) continue;
      const res = await PermissionAPI.grant(uid, pid, allowed);
      if (res !== null) {
        setBoundMap((prev) => {
          const next = new Map(prev);
          next.set(p.code, {
            user_permission_id:
              res?.user_permission_id ??
              res?.id ??
              existing?.user_permission_id,
            allowed,
          });
          return next;
        });
      }
    }
    flash(
      true,
      `${mod.replace(/_/g, " ")} set to ${allowed ? "Allowed" : "Denied"}`,
    );
    setSavingModule(null);
  }

  const visiblePerms = search.trim()
    ? allPerms.filter((p) =>
        `${p.name || ""} ${p.code || ""} ${p.module || ""}`
          .toLowerCase()
          .includes(search.toLowerCase()),
      )
    : allPerms;

  const grouped = {};
  for (const p of visiblePerms) {
    const mod = p.module || "OTHER";
    if (!grouped[mod]) grouped[mod] = [];
    grouped[mod].push(p);
  }
  const modules = Object.keys(grouped).sort();

  // Denied = explicit override with allowed:false. Everything else
  // (no record, or explicit allowed:true) counts as Allowed.
  const deniedCount = allPerms.filter(
    (p) => boundMap.get(p.code)?.allowed === false,
  ).length;
  const allowedCount = allPerms.length - deniedCount;

  if (loading)
    return (
      <div className="empty" style={{ padding: "32px 0" }}>
        Loading permissions…
      </div>
    );

  return (
    <div>
      {!canUpdatePermissions && (
        <ReadOnlyBanner message="You have view-only access to user permissions." />
      )}

      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 14,
          flexWrap: "wrap",
        }}
      >
        <div className="tsearch" style={{ flex: 1, minWidth: 180 }}>
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search permissions…"
          />
        </div>
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <span
            style={{ fontSize: 12, color: "var(--success)", fontWeight: 600 }}
          >
            {allowedCount} allowed
          </span>
          <span style={{ fontSize: 12, color: "var(--text3)" }}>·</span>
          <span
            style={{ fontSize: 12, color: "var(--danger)", fontWeight: 600 }}
          >
            {deniedCount} denied
          </span>
          <span style={{ fontSize: 12, color: "var(--text3)" }}>·</span>
          <span style={{ fontSize: 12, color: "var(--text3)" }}>
            {allPerms.length} total
          </span>
        </div>
      </div>

      {/* Flash message */}
      {flashMsg && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 12,
            background:
              flashMsg.type === "success"
                ? "rgba(0,230,118,0.10)"
                : "rgba(255,23,68,0.10)",
            border: `1px solid ${flashMsg.type === "success" ? "rgba(0,230,118,0.35)" : "rgba(255,23,68,0.35)"}`,
            borderRadius: 8,
            padding: "9px 14px",
            fontSize: 12,
            fontWeight: 600,
            color:
              flashMsg.type === "success" ? "var(--success)" : "var(--danger)",
            animation: "toast-enter 0.2s ease",
          }}
        >
          <span style={{ fontSize: 14 }}>
            {flashMsg.type === "success" ? "✓" : "✗"}
          </span>
          <span>{flashMsg.text}</span>
        </div>
      )}

      {/* Module-grouped permission list */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {modules.map((mod) => {
          const perms = grouped[mod];
          const isOpen = !collapsed[mod];
          const modDenied = perms.filter(
            (p) => boundMap.get(p.code)?.allowed === false,
          ).length;
          const modAllowed = perms.length - modDenied;
          const modBusy = savingModule === mod;

          return (
            <div
              key={mod}
              style={{
                background: "var(--elevated)",
                border: "1px solid var(--border)",
                borderRadius: 10,
                overflow: "hidden",
              }}
            >
              {/* Module header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "11px 14px",
                  userSelect: "none",
                  background: isOpen
                    ? "var(--surface)"
                    : "var(--elevated)",
                  borderBottom: isOpen ? "1px solid var(--border)" : "none",
                }}
              >
                <span
                  onClick={() => toggleCollapse(mod)}
                  style={{
                    flex: 1,
                    fontSize: 12,
                    fontWeight: 700,
                    color: "var(--text1)",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    cursor: "pointer",
                  }}
                >
                  {mod.replace(/_/g, " ")}
                </span>

                <span
                  style={{
                    fontSize: 11,
                    color: "var(--success)",
                    fontWeight: 600,
                    background: "rgba(0,230,118,0.10)",
                    border: "1px solid rgba(0,230,118,0.28)",
                    borderRadius: 6,
                    padding: "2px 8px",
                  }}
                >
                  {modAllowed} allowed
                </span>
                <span
                  style={{
                    fontSize: 11,
                    color: "var(--danger)",
                    fontWeight: 600,
                    background: "rgba(255,23,68,0.10)",
                    border: "1px solid rgba(255,23,68,0.28)",
                    borderRadius: 6,
                    padding: "2px 8px",
                  }}
                >
                  {modDenied} denied
                </span>

                {canUpdatePermissions && (
                  <div style={{ display: "flex", gap: 6, marginLeft: 4 }}>
                    <button
                      className="btn btn-g btn-sm"
                      disabled={modBusy}
                      onClick={() =>
                        handleSetAllowedForModule(mod, perms, true)
                      }
                      style={{ fontSize: 11, padding: "3px 9px" }}
                    >
                      {modBusy ? <span className="spin-icon" /> : "Allow all"}
                    </button>
                    <button
                      className="btn btn-g btn-sm"
                      disabled={modBusy}
                      onClick={() =>
                        handleSetAllowedForModule(mod, perms, false)
                      }
                      style={{ fontSize: 11, padding: "3px 9px" }}
                    >
                      {modBusy ? <span className="spin-icon" /> : "Deny all"}
                    </button>
                  </div>
                )}

                <svg
                  onClick={() => toggleCollapse(mod)}
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  style={{
                    transition: "transform 0.18s",
                    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                    cursor: "pointer",
                    flexShrink: 0,
                    color: "var(--text3)",
                  }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>

              {/* Permission rows */}
              {isOpen && (
                <div>
                  {perms.map((p, idx) => {
                    const boundInfo = boundMap.get(p.code);
                    // No explicit record => Allowed by default (group-derived baseline).
                    // Only an explicit allowed:false record flips it to Denied.
                    const isDenied = boundInfo?.allowed === false;
                    const isAllowed = !isDenied;
                    const rowBusy = savingCode === p.code;

                    return (
                      <div
                        key={p.code}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          background: "var(--bright)",
                          gap: 12,
                          padding: "10px 14px",
                          borderBottom:
                            idx < perms.length - 1
                              ? "1px solid var(--border)"
                              : "none",
                          opacity: rowBusy ? 0.6 : 1,
                          transition: "opacity 0.12s",
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: "var(--text1)",
                              lineHeight: 1.35,
                            }}
                          >
                            {p.name || p.permission_name || p.code}
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              color: "var(--text3)",
                              marginTop: 2,
                              fontFamily: "monospace",
                              letterSpacing: "0.04em",
                            }}
                          >
                            {p.code}
                          </div>
                        </div>

                        {p.action && (
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: "0.06em",
                              padding: "2px 7px",
                              borderRadius: 5,
                              background: actionColor(p.action).bg,
                              color: actionColor(p.action).fg,
                              flexShrink: 0,
                            }}
                          >
                            {p.action}
                          </span>
                        )}

                        {/* ── Radio group: Allow / Deny ── */}
                        <div
                          role="radiogroup"
                          aria-label={`${p.code} allowed`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 14,
                            flexShrink: 0,
                            minWidth: 140,
                            justifyContent: "flex-end",
                          }}
                        >
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 5,
                              cursor:
                                canUpdatePermissions && !rowBusy
                                  ? "pointer"
                                  : "default",
                              fontSize: 11,
                              fontWeight: 600,
                              color: isAllowed
                                ? "var(--success)"
                                : "var(--text3)",
                            }}
                          >
                            <input
                              type="radio"
                              name={`perm-${p.code}`}
                              checked={isAllowed}
                              disabled={!canUpdatePermissions || rowBusy}
                              onChange={() => handleSetAllowed(p, true)}
                              style={{
                                accentColor: "var(--success)",
                                cursor: "inherit",
                              }}
                            />
                            Allow
                          </label>
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 5,
                              cursor:
                                canUpdatePermissions && !rowBusy
                                  ? "pointer"
                                  : "default",
                              fontSize: 11,
                              fontWeight: 600,
                              color: isDenied
                                ? "var(--danger)"
                                : "var(--text3)",
                            }}
                          >
                            <input
                              type="radio"
                              name={`perm-${p.code}`}
                              checked={isDenied}
                              disabled={!canUpdatePermissions || rowBusy}
                              onChange={() => handleSetAllowed(p, false)}
                              style={{
                                accentColor: "var(--danger)",
                                cursor: "inherit",
                              }}
                            />
                            Deny
                          </label>
                          {rowBusy && (
                            <span
                              className="spin-icon"
                              style={{ width: 12, height: 12 }}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── BRANCHES PANEL ────────────────────────────────────── */
function EditBranchesPanel({ user, allBranches = [] }) {
  const { canUpdateUsers } = usePermissions();

  const uid = resolveUserId(user);
  const [boundBranches, setBoundBranches] = useState([]);
  const [unboundBranches, setUnboundBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);

    // ── FIXED: use the correct endpoint for user-assigned branches ──
    const [boundRes, allRes] = await Promise.all([
      apiFetch("POST", "/api/v1/amis/branch/by-user-id", { user_id: uid }),
      typeof BranchAPI.getAll === "function"
        ? BranchAPI.getAll()
        : Promise.resolve(null),
    ]);

    const boundArr = Array.isArray(boundRes)
      ? boundRes
      : boundRes?.data || boundRes?.branches || (boundRes ? [boundRes] : []);

    // Fall back to the prop if the API call returned nothing
    let allArr = allBranches;
    if (allRes) {
      allArr = Array.isArray(allRes)
        ? allRes
        : allRes?.data || allRes?.branches || allBranches;
    }

    const boundIds = new Set(boundArr.map((b) => String(b.branch_id ?? b.id)));
    setBoundBranches(boundArr);
    setUnboundBranches(
      allArr.filter((b) => !boundIds.has(String(b.branch_id ?? b.id))),
    );
    setLoading(false);
  }, [uid, allBranches]);

  useEffect(() => {
    load();
  }, [load]);

  function flash(ok) {
    setSaveStatus(ok ? "saved" : "error");
    setTimeout(() => setSaveStatus(null), 3000);
  }

  async function bindBranch(bid) {
    if (!canUpdateUsers) return;
    setSaving(bid);
    const res = await apiFetch("POST", "/api/v1/amis/branch/bind", {
      branch_id: Number(bid),
      user_id: uid,
    });
    flash(res !== null);
    await load();
    setSaving(null);
  }

  async function unbindBranch(bid) {
    if (!canUpdateUsers) return;
    setSaving(bid);
    const res = await apiFetch("POST", "/api/v1/amis/branch/unbind", {
      branch_id: Number(bid),
      user_id: uid,
    });
    flash(res !== null);
    await load();
    setSaving(null);
  }

  const matchSearch = (b) =>
    `${b.branch_name || b.name || ""} ${b.region || b.location || ""}`
      .toLowerCase()
      .includes(search.toLowerCase());

  if (loading)
    return (
      <div className="empty" style={{ padding: "32px 0" }}>
        Loading branches…
      </div>
    );

  return (
    <div>
      {!canUpdateUsers && (
        <ReadOnlyBanner message="You have view-only access to branch assignments." />
      )}

      <div style={{ marginBottom: 12 }}>
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search branches…"
        />
      </div>
      <SaveBanner status={saveStatus} />
      <div className="g2" style={{ alignItems: "start", marginTop: 12 }}>
        {canUpdateUsers && (
          <FormCard label={`Available Branches (${unboundBranches.length})`}>
            {unboundBranches.filter(matchSearch).length === 0 ? (
              <div className="empty">All branches already assigned.</div>
            ) : (
              <DataTable
                columns={["Branch", "Action"]}
                rows={unboundBranches.filter(matchSearch).map((b) => {
                  const bid = b.branch_id ?? b.id;
                  return (
                    <tr key={bid}>
                      <td className="fw-6">{b.branch_name || b.name || "-"}</td>
                      <td>
                        <Btn
                          variant="p"
                          sm
                          onClick={() => bindBranch(bid)}
                          disabled={saving === bid}
                        >
                          {saving === bid ? (
                            <span className="spin-icon" />
                          ) : (
                            "+ Assign"
                          )}
                        </Btn>
                      </td>
                    </tr>
                  );
                })}
              />
            )}
          </FormCard>
        )}

        <FormCard label={`Assigned Branches (${boundBranches.length})`}>
          {boundBranches.filter(matchSearch).length === 0 ? (
            <div className="empty">No branches assigned yet.</div>
          ) : (
            <DataTable
              columns={canUpdateUsers ? ["Branch", "Action"] : ["Branch"]}
              rows={boundBranches.filter(matchSearch).map((b) => {
                const bid = b.branch_id ?? b.id;
                return (
                  <tr key={bid}>
                    <td className="fw-6">{b.branch_name || b.name || "-"}</td>
                    {canUpdateUsers && (
                      <td>
                        <Btn
                          variant="d"
                          sm
                          onClick={() => unbindBranch(bid)}
                          disabled={saving === bid}
                        >
                          {saving === bid ? (
                            <span className="spin-icon" />
                          ) : (
                            "Remove"
                          )}
                        </Btn>
                      </td>
                    )}
                  </tr>
                );
              })}
            />
          )}
        </FormCard>
      </div>
    </div>
  );
}

/* ─── WAREHOUSES PANEL ──────────────────────────────────── */
function EditWarehousesPanel({ user }) {
  const { canUpdateUsers } = usePermissions();

  const uid = resolveUserId(user);
  const [boundWarehouses, setBoundWarehouses] = useState([]);
  const [unboundWarehouses, setUnboundWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const [allRes, boundRes] = await Promise.all([
      WarehouseAPI.getAll(),
      WarehouseAPI.getByUser(uid),
    ]);
    const allArr = Array.isArray(allRes)
      ? allRes
      : allRes?.data || allRes?.warehouses || [];
    const boundArr = Array.isArray(boundRes)
      ? boundRes
      : boundRes?.data || boundRes?.warehouses || (boundRes ? [boundRes] : []);
    const boundIds = new Set(
      boundArr.map((w) => String(w.warehouse_id ?? w.id)),
    );
    setBoundWarehouses(boundArr);
    setUnboundWarehouses(
      allArr.filter((w) => !boundIds.has(String(w.warehouse_id ?? w.id))),
    );
    setLoading(false);
  }, [uid]);

  useEffect(() => {
    load();
  }, [load]);

  function flash(ok) {
    setSaveStatus(ok ? "saved" : "error");
    setTimeout(() => setSaveStatus(null), 3000);
  }

  async function bindWarehouse(wid) {
    if (!canUpdateUsers) return;
    setSaving(wid);
    const res = await apiFetch("POST", "/api/v1/amis/warehouse/bind", {
      warehouse_id: Number(wid),
      user_id: uid,
    });
    flash(res !== null);
    await load();
    setSaving(null);
  }

  async function unbindWarehouse(wid) {
    if (!canUpdateUsers) return;
    setSaving(wid);
    const res = await apiFetch("POST", "/api/v1/amis/warehouse/unbind", {
      warehouse_id: Number(wid),
      user_id: uid,
    });
    flash(res !== null);
    await load();
    setSaving(null);
  }

  const matchSearch = (w) =>
    `${w.warehouse_name || w.name || ""} ${w.branch?.branch_name || w.branch || ""}`
      .toLowerCase()
      .includes(search.toLowerCase());

  if (loading)
    return (
      <div className="empty" style={{ padding: "32px 0" }}>
        Loading warehouses…
      </div>
    );

  return (
    <div>
      {!canUpdateUsers && (
        <ReadOnlyBanner message="You have view-only access to warehouse assignments." />
      )}

      <div style={{ marginBottom: 12 }}>
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search warehouses…"
        />
      </div>
      <SaveBanner status={saveStatus} />
      <div className="g2" style={{ alignItems: "start", marginTop: 12 }}>
        {canUpdateUsers && (
          <FormCard
            label={`Available Warehouses (${unboundWarehouses.length})`}
          >
            {unboundWarehouses.filter(matchSearch).length === 0 ? (
              <div className="empty">All warehouses already assigned.</div>
            ) : (
              <DataTable
                columns={["Warehouse", "Branch", "Action"]}
                rows={unboundWarehouses.filter(matchSearch).map((w) => {
                  const wid = w.warehouse_id ?? w.id;
                  return (
                    <tr key={wid}>
                      <td className="fw-6">
                        {w.warehouse_name || w.name || "-"}
                      </td>
                      <td className="tc3">
                        {w.branch?.branch_name || w.branch || "-"}
                      </td>
                      <td>
                        <Btn
                          variant="p"
                          sm
                          onClick={() => bindWarehouse(wid)}
                          disabled={saving === wid}
                        >
                          {saving === wid ? (
                            <span className="spin-icon" />
                          ) : (
                            "+ Assign"
                          )}
                        </Btn>
                      </td>
                    </tr>
                  );
                })}
              />
            )}
          </FormCard>
        )}

        <FormCard label={`Assigned Warehouses (${boundWarehouses.length})`}>
          {boundWarehouses.filter(matchSearch).length === 0 ? (
            <div className="empty">No warehouses assigned yet.</div>
          ) : (
            <DataTable
              columns={
                canUpdateUsers
                  ? ["Warehouse", "Branch", "Status", "Action"]
                  : ["Warehouse", "Branch", "Status"]
              }
              rows={boundWarehouses.filter(matchSearch).map((w) => {
                const wid = w.warehouse_id ?? w.id;
                return (
                  <tr key={wid}>
                    <td className="fw-6">
                      {w.warehouse_name || w.name || "-"}
                    </td>
                    <td className="tc3">
                      {w.branch?.branch_name || w.branch || "-"}
                    </td>
                    <td>
                      <Badge color={w.is_active ? "green" : "red"}>
                        {w.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </td>
                    {canUpdateUsers && (
                      <td>
                        <Btn
                          variant="d"
                          sm
                          onClick={() => unbindWarehouse(wid)}
                          disabled={saving === wid}
                        >
                          {saving === wid ? (
                            <span className="spin-icon" />
                          ) : (
                            "Remove"
                          )}
                        </Btn>
                      </td>
                    )}
                  </tr>
                );
              })}
            />
          )}
        </FormCard>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   ADD USER PAGE
═══════════════════════════════════════════════════════ */
function AddUserPage({
  branches,
  warehouses = [],
  users = [],
  onSave,
  onBack,
}) {
  const { canCreateUsers } = usePermissions();

  const [d, setD] = useState({
    group: [],
    permIds: [],
    status: "Active",
    warehouse: [],
    branch: [],
  });
  const [allGroups, setAllGroups] = useState([]);
  const [allPerms, setAllPerms] = useState([]);
  const [permAllowedMap, setPermAllowedMap] = useState({});
  const [loading, setLoading] = useState(false);
  const upd = (k, v) => setD((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    GroupAPI.getAll().then((res) =>
      setAllGroups(Array.isArray(res) ? res : res?.groups || res?.data || []),
    );
    PermissionAPI.getAll().then((res) =>
      setAllPerms(
        Array.isArray(res) ? res : res?.permissions || res?.data || [],
      ),
    );
  }, []);

  const groupOpts = allGroups.map((g) => ({
    value: String(g.group_id ?? g.id),
    label: g.name || g.group_name || String(g.group_id ?? g.id),
  }));
  const permOpts = allPerms.map((p) => ({
    value: String(p.permission_id ?? p.id),
    label:
      p.name ||
      p.permission_name ||
      p.description ||
      String(p.permission_id ?? p.id),
  }));
  const branchOpts = branches.map((b) => ({
    value: String(b.id),
    label: b.name,
  }));
  const warehouseOpts = warehouses
    .filter(
      (w) =>
        !d.branch?.length ||
        (Array.isArray(d.branch)
          ? d.branch.includes(String(w.branch_id))
          : w.branch === d.branch),
    )
    .map((w) => ({ value: String(w.id), label: `${w.name} (${w.branch})` }));

  async function submit() {
    if (!canCreateUsers) return;
    if (!d.fname?.trim()) {
      alert("First name is required.");
      return;
    }
    if (!d.email?.trim()) {
      alert("Email is required.");
      return;
    }
    setLoading(true);
    const payload = {
      name: d.fname.trim(),
      surname: (d.lname || "").trim(),
      email_address: d.email.trim(),
      contact_number: d.phone?.trim() || "",
      is_active: d.status === "Active",
      organization_id: getOrgId(),
      groups: d.group.map((gid) => ({ group_id: Number(gid) })),
      explicit_permissions: d.permIds.map((pid) => ({
        permission_id: Number(pid),
        allowed: permAllowedMap[pid] !== false,
      })),
    };
    const res = await UserAPI.create(payload);
    const created = res || { id: Date.now(), ...payload };
    const newUserId = resolveUserId(res) ?? created.user_id ?? created.id;
    if (Array.isArray(d.branch) && d.branch.length > 0 && newUserId) {
      for (const bid of d.branch)
        await apiFetch("POST", "/api/v1/amis/branch/bind", {
          branch_id: Number(bid),
          user_id: newUserId,
        });
    }
    if (Array.isArray(d.warehouse) && d.warehouse.length > 0 && newUserId) {
      for (const wid of d.warehouse)
        await apiFetch("POST", "/api/v1/amis/warehouse/bind", {
          warehouse_id: Number(wid),
          user_id: newUserId,
        });
    }
    onSave(created);
    setLoading(false);
  }

  return (
    <div className="page action-page" style={{ maxWidth: 1600 }}>
      <div className="mb-22">
        <button className="back-btn" onClick={onBack}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path d="M19 12H5" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back
        </button>
        <div className="action-page-header" style={{ marginTop: 24 }}>
          <p className="action-page-subtitle">
            Create a user account and assign access
          </p>
        </div>
      </div>
      <FormCard label="Personal Information">
        <div className="g2">
          <FG label="First Name">
            <Inp
              value={d.fname}
              onChange={(v) => upd("fname", v)}
              placeholder="First name"
            />
          </FG>
          <FG label="Last Name">
            <Inp
              value={d.lname}
              onChange={(v) => upd("lname", v)}
              placeholder="Last name"
            />
          </FG>
        </div>
        <FG label="Email Address">
          <Inp
            value={d.email}
            onChange={(v) => upd("email", v)}
            placeholder="user@company.co.za"
            type="email"
          />
        </FG>
        <FG label="Contact Number">
          <Inp
            value={d.phone}
            onChange={(v) => upd("phone", v)}
            placeholder="Contact number"
          />
        </FG>
      </FormCard>
      <FormCard label="Groups">
        <FG label="Assign to Group(s)">
          <MultiSelect
            value={d.group}
            onChange={(v) => upd("group", v)}
            options={
              groupOpts.length
                ? groupOpts
                : [{ value: "", label: "No groups available" }]
            }
            placeholder="Select group(s)…"
          />
        </FG>
      </FormCard>
      <FormCard label="Explicit Permissions">
        <FG label="Assign Permission(s)">
          <MultiSelect
            value={d.permIds}
            onChange={(v) => upd("permIds", v)}
            options={
              permOpts.length
                ? permOpts
                : [{ value: "", label: "No permissions available" }]
            }
            placeholder="Select permission(s)…"
          />
        </FG>
        {d.permIds.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div className="fl mb-4">
              Set allowed / denied for each permission
            </div>
            {d.permIds.map((pid) => {
              const p = allPerms.find(
                (x) => String(x.permission_id ?? x.id) === pid,
              );
              const label = p
                ? p.name || p.permission_name || p.description || pid
                : pid;
              return (
                <div
                  key={pid}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 6,
                  }}
                >
                  <span className="tc3 fs12" style={{ minWidth: 180 }}>
                    {label}
                  </span>
                  <Sel
                    value={String(permAllowedMap[pid] !== false)}
                    onChange={(v) =>
                      setPermAllowedMap((prev) => ({
                        ...prev,
                        [pid]: v === "true",
                      }))
                    }
                    options={[
                      { value: "true", label: "Allowed" },
                      { value: "false", label: "Denied" },
                    ]}
                  />
                </div>
              );
            })}
          </div>
        )}
      </FormCard>
      <FormCard label="Assignment (Optional)">
        <div className="g2">
          <FG label="Branch">
            <MultiSelect
              value={d.branch}
              onChange={(v) => upd("branch", v)}
              options={branchOpts}
              placeholder="Select branch(es)…"
            />
          </FG>
          <FG label="Assigned Warehouse">
            <MultiSelect
              value={d.warehouse}
              onChange={(v) => upd("warehouse", v)}
              options={warehouseOpts}
              placeholder="Select warehouse(s)…"
            />
          </FG>
        </div>
        <FG label="Account Status">
          <Sel
            value={d.status}
            onChange={(v) => upd("status", v)}
            options={[
              { value: "Active", label: "Active" },
              { value: "Inactive", label: "Inactive" },
            ]}
          />
        </FG>
      </FormCard>
      <FormFooter
        onCancel={onBack}
        onSubmit={submit}
        loading={loading}
        label="Create User"
        disabled={!canCreateUsers}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   DELETE USER PAGE
═══════════════════════════════════════════════════════ */
function DeleteUserPage({ user, onConfirm, onBack }) {
  const { canDeleteUsers } = usePermissions();
  const [loading, setLoading] = useState(false);

  async function confirm() {
    if (!canDeleteUsers) return;
    setLoading(true);
    const uid = resolveUserId(user);
    await UserAPI.delete(uid);
    onConfirm(uid);
    setLoading(false);
  }

  return (
    <div className="page action-page" style={{ maxWidth: 1600 }}>
      <div className="mb-22">
        <button className="back-btn" onClick={onBack}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path d="M19 12H5" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back
        </button>
        <div className="action-page-header" style={{ marginTop: 24 }}>
          <p className="action-page-subtitle">
            This action is permanent and cannot be undone
          </p>
          <div className="action-page-subject">{user.name}</div>
        </div>
      </div>
      <div className="danger-zone mb-16">
        <div className="danger-zone-title">⚠ Danger Zone</div>
        <div className="danger-zone-msg">
          Permanently deletes this user account, sessions, and associated data.
        </div>
      </div>
      <FormCard label="User to be Deleted">
        <ReviewBlock
          data={{
            Name: user.name,
            Email: user.email,
            Group: user.group,
            Branch: user.branch,
            Status: user.status,
          }}
        />
      </FormCard>
      <FormFooter
        onCancel={onBack}
        onSubmit={confirm}
        loading={loading}
        label="Confirm Delete"
        danger
        disabled={!canDeleteUsers}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   UPDATE PASSWORD PAGE
═══════════════════════════════════════════════════════ */
function UpdatePasswordPage({ user, onSave, onBack }) {
  const { canUpdateUsers } = usePermissions();

  const [secret, setSecret] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit() {
    if (!canUpdateUsers) return;
    if (!secret || secret.length < 6) {
      alert("Password must be at least 6 characters.");
      return;
    }
    if (secret !== confirm) {
      alert("Passwords do not match.");
      return;
    }
    setLoading(true);
    const uid = resolveUserId(user);
    const res = await UserAPI.updatePassword(uid, secret);
    if (res) onSave(res);
    else alert("Failed to update password");
    setLoading(false);
  }

  return (
    <div className="page action-page" style={{ maxWidth: 1600 }}>
      <div className="mb-22">
        <button className="back-btn" onClick={onBack}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path d="M19 12H5" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back
        </button>
        <div className="action-page-header" style={{ marginTop: 24 }}>
          <p className="action-page-subtitle">
            Set a new password for this user
          </p>
          <div className="action-page-subject">{user.name}</div>
        </div>
      </div>
      <FormCard label="New Password">
        <FG label="Password">
          <Inp
            value={secret}
            onChange={setSecret}
            placeholder="New password"
            type="password"
            disabled={!canUpdateUsers}
          />
        </FG>
        <FG label="Confirm Password">
          <Inp
            value={confirm}
            onChange={setConfirm}
            placeholder="Confirm password"
            type="password"
            disabled={!canUpdateUsers}
          />
        </FG>
      </FormCard>
      <FormFooter
        onCancel={onBack}
        onSubmit={submit}
        loading={loading}
        label="Update Password"
        disabled={!canUpdateUsers}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   USERS LIST SECTION
═══════════════════════════════════════════════════════ */
function UsersSection({ users, branches, warehouses, onNavigate, toast }) {
  const { canCreateUsers, canUpdateUsers, canDeleteUsers } = usePermissions();

  const [search, setSearch] = useState("");
  const filtered = users.filter((u) =>
    `${u.name} ${u.email} ${u.group} ${u.branch}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );

  return (
    <div className="panel">
      <div className="tbar">
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search users…"
        />
        <div className="flex-1" />
        {canCreateUsers && (
          <Btn variant="p" sm onClick={() => onNavigate("addUser")}>
            ＋ Add User
          </Btn>
        )}
      </div>
      <DataTable
        columns={["User", "Email", "Status", "Actions"]}
        rows={filtered.map((u) => {
          const actionItems = [
            ...(canUpdateUsers
              ? [
                  {
                    label: " Reset Password",
                    onClick: () => onNavigate("updatePassword", u),
                  },
                ]
              : []),
            ...(canDeleteUsers
              ? [
                  {
                    label: "Delete",
                    onClick: () => onNavigate("deleteUser", u),
                    danger: true,
                  },
                ]
              : []),
          ];

          return (
            <tr key={u.id}>
              <td>
                <span className="fw-6">{u.name}</span>
              </td>
              <td className="mono">{u.email}</td>
              <td>
                <Badge color={u.status === "Active" ? "green" : "red"}>
                  {u.status}
                </Badge>
              </td>
              <td>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {canUpdateUsers && (
                    <Btn
                      variant="s"
                      sm
                      onClick={() => onNavigate("editUser", u)}
                    >
                      Edit
                    </Btn>
                  )}
                  {actionItems.length > 0 && <ActionMenu items={actionItems} />}
                </div>
              </td>
            </tr>
          );
        })}
        emptyMsg="No users found"
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   GROUPS SECTION
═══════════════════════════════════════════════════════ */
function GroupsSection({ onNavigate, toast }) {
  const {
    canCreateGroups,
    canUpdateGroups,
    canDeleteGroups,
    canUpdateUserGroup,
    canUpdateAccessGroup,
  } = usePermissions();

  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await GroupAPI.getAll();
    setGroups(Array.isArray(data) ? data : data?.groups || data?.data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = groups.filter((g) =>
    `${g.name || ""} ${g.description || ""}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );

  async function handleDelete(group) {
    if (!canDeleteGroups) return;
    if (!window.confirm(`Delete group "${group.name}"? This cannot be undone.`))
      return;
    const res = await GroupAPI.delete(group.group_id ?? group.id);
    if (res !== null) {
      setGroups((prev) =>
        prev.filter(
          (g) => (g.group_id ?? g.id) !== (group.group_id ?? group.id),
        ),
      );
      toast("Group deleted", "warning");
    } else {
      toast("Failed to delete group", "danger");
    }
  }

  return (
    <div className="panel">
      <div className="tbar">
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search groups…"
        />
        <div className="flex-1" />
        {canCreateGroups && (
          <Btn variant="p" sm onClick={() => onNavigate("addGroup")}>
            ＋ Add Group
          </Btn>
        )}
      </div>
      {loading ? (
        <div className="empty" style={{ padding: "24px 0" }}>
          Loading groups…
        </div>
      ) : (
        <DataTable
          columns={["Group Name", "Description", "Actions"]}
          rows={filtered.map((g) => {
            const gid = g.group_id ?? g.id;

            const actionItems = [
              ...(canUpdateUserGroup
                ? [
                    {
                      label: " Manage Members",
                      onClick: () => onNavigate("manageGroupMembers", g),
                    },
                  ]
                : []),
              ...(canUpdateAccessGroup
                ? [
                    {
                      label: " Manage Permissions",
                      onClick: () => onNavigate("manageGroupPermissions", g),
                    },
                  ]
                : []),
              ...(canDeleteGroups
                ? [
                    {
                      label: " Delete",
                      onClick: () => handleDelete(g),
                      danger: true,
                    },
                  ]
                : []),
            ];

            return (
              <tr key={gid}>
                <td>
                  <span className="fw-6">{g.name || g.group_name || "-"}</span>
                </td>
                <td className="tc3">{g.description || "-"}</td>
                <td>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    {canUpdateGroups && (
                      <Btn
                        variant="s"
                        sm
                        onClick={() => onNavigate("editGroup", g)}
                      >
                        Edit
                      </Btn>
                    )}
                    {actionItems.length > 0 && (
                      <ActionMenu items={actionItems} />
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
          emptyMsg="No groups found"
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   ADD GROUP PAGE
═══════════════════════════════════════════════════════ */
function AddGroupPage({ onSave, onBack }) {
  const { canCreateGroups } = usePermissions();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit() {
    if (!canCreateGroups) return;
    if (!name.trim()) {
      alert("Group name is required.");
      return;
    }
    setLoading(true);
    const res = await GroupAPI.create(
      name.trim(),
      description.trim(),
      getOrgId(),
    );
    onSave(res || { name, description });
    setLoading(false);
  }

  return (
    <div className="page action-page" style={{ maxWidth: 1600 }}>
      <div className="mb-22">
        <button className="back-btn" onClick={onBack}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path d="M19 12H5" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back
        </button>
        <div className="action-page-header" style={{ marginTop: 24 }}>
          <p className="action-page-subtitle">Create a new user group</p>
        </div>
      </div>
      <FormCard label="Group Details">
        <FG label="Group Name">
          <Inp
            value={name}
            onChange={setName}
            placeholder="e.g. Warehouse Staff"
          />
        </FG>
        <FG label="Description">
          <Inp
            value={description}
            onChange={setDescription}
            placeholder="Optional description"
          />
        </FG>
      </FormCard>
      <FormFooter
        onCancel={onBack}
        onSubmit={submit}
        loading={loading}
        label="Create Group"
        disabled={!canCreateGroups}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   EDIT GROUP PAGE
═══════════════════════════════════════════════════════ */
function EditGroupPage({ group, onSave, onBack }) {
  const { canUpdateGroups } = usePermissions();

  const [name, setName] = useState(group.name || group.group_name || "");
  const [description, setDescription] = useState(group.description || "");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const gid = group.group_id ?? group.id;
    if (!gid) return;
    GroupAPI.getById(gid).then((fresh) => {
      if (fresh) {
        setName(fresh.name || fresh.group_name || name);
        setDescription(fresh.description || description);
      }
    });
  }, [group.group_id ?? group.id]);

  async function submit() {
    if (!canUpdateGroups) return;
    if (!name.trim()) {
      alert("Group name is required.");
      return;
    }
    setLoading(true);
    const gid = group.group_id ?? group.id;
    const res = await GroupAPI.update(
      gid,
      name.trim(),
      description.trim(),
      getOrgId(),
    );
    onSave(res || { ...group, name, description });
    setLoading(false);
  }

  return (
    <div className="page action-page" style={{ maxWidth: 1600 }}>
      <div className="mb-22">
        <button className="back-btn" onClick={onBack}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path d="M19 12H5" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back
        </button>
        <div className="action-page-header" style={{ marginTop: 24 }}>
          <p className="action-page-subtitle">Update group details</p>
          <div className="action-page-subject">
            {group.name || group.group_name}
          </div>
        </div>
      </div>
      <FormCard label="Group Details">
        <FG label="Group Name">
          <Inp value={name} onChange={setName} disabled={!canUpdateGroups} />
        </FG>
        <FG label="Description">
          <Inp
            value={description}
            onChange={setDescription}
            placeholder="Optional description"
            disabled={!canUpdateGroups}
          />
        </FG>
      </FormCard>
      <FormFooter
        onCancel={onBack}
        onSubmit={submit}
        loading={loading}
        label="Save Changes"
        disabled={!canUpdateGroups}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   MANAGE GROUP MEMBERS PAGE
═══════════════════════════════════════════════════════ */
function ManageGroupMembersPage({ group, toast, onBack }) {
  const { canUpdateUserGroup } = usePermissions();

  const gid = group.group_id ?? group.id;
  const [boundUsers, setBoundUsers] = useState([]);
  const [unboundUsers, setUnboundUsers] = useState([]);
  const [selectedBound, setSelectedBound] = useState([]);
  const [selectedUnbound, setSelectedUnbound] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [boundRes, unboundRes] = await Promise.all([
      GroupAPI.getBoundUsers(gid),
      GroupAPI.getUnboundUsers(gid),
    ]);
    const norm = (r) => {
      if (!r) return [];
      if (Array.isArray(r)) return r;
      if (Array.isArray(r.users)) return r.users;
      if (Array.isArray(r.data)) return r.data;
      return [];
    };
    setBoundUsers(norm(boundRes));
    setUnboundUsers(norm(unboundRes));
    setLoading(false);
  }, [gid]);

  useEffect(() => {
    load();
  }, [load]);

  const displayName = (u) =>
    u.name ||
    [u.name, u.surname].filter(Boolean).join(" ") ||
    u.fullname ||
    u.email_address ||
    u.email ||
    String(u.user_id ?? u.id ?? "?");

  async function bindSelected() {
    if (!canUpdateUserGroup || !selectedUnbound.length) return;
    setSaving(true);
    for (const uid of selectedUnbound)
      await GroupAPI.bindUser(Number(uid), gid);
    toast(`Added ${selectedUnbound.length} user(s) to group`, "success");
    setSelectedUnbound([]);
    await load();
    setSaving(false);
  }

  async function unbindSelected() {
    if (!canUpdateUserGroup || !selectedBound.length) return;
    setSaving(true);
    for (const uid of selectedBound)
      await GroupAPI.unbindUser(Number(uid), gid);
    toast(`Removed ${selectedBound.length} user(s) from group`, "warning");
    setSelectedBound([]);
    await load();
    setSaving(false);
  }

  const boundOpts = boundUsers.map((u) => ({
    value: String(u.user_id ?? u.id),
    label: displayName(u),
  }));
  const unboundOpts = unboundUsers.map((u) => ({
    value: String(u.user_id ?? u.id),
    label: displayName(u),
  }));

  return (
    <div className="page action-page" style={{ maxWidth: 1600 }}>
      <div className="mb-22">
        <div className="action-page-header" style={{ marginTop: 24 }}>
          <p className="action-page-subtitle">
            {canUpdateUserGroup
              ? "Add or remove users from this group"
              : "View members of this group"}
          </p>
          <div className="action-page-subject">
            {group.name || group.group_name}
          </div>
        </div>
      </div>

      {!canUpdateUserGroup && (
        <ReadOnlyBanner message="You have view-only access to group membership." />
      )}

      {loading ? (
        <div className="empty" style={{ padding: "32px 0" }}>
          Loading members…
        </div>
      ) : (
        <div className="g2" style={{ alignItems: "start" }}>
          {canUpdateUserGroup && (
            <FormCard label={`Available Users (${unboundUsers.length})`}>
              <FG label="Select users to add">
                <MultiSelect
                  value={selectedUnbound}
                  onChange={setSelectedUnbound}
                  options={
                    unboundOpts.length
                      ? unboundOpts
                      : [
                          {
                            value: "__none__",
                            label: "All users already in group",
                          },
                        ]
                  }
                  placeholder="Choose users to add…"
                />
              </FG>
              <div className="fb gap-8 pt-8">
                <Btn
                  variant="p"
                  onClick={bindSelected}
                  disabled={saving || !selectedUnbound.length}
                >
                  {saving ? <span className="spin-icon" /> : "Add to Group →"}
                </Btn>
              </div>
            </FormCard>
          )}

          <FormCard label={`Current Members (${boundUsers.length})`}>
            {canUpdateUserGroup ? (
              <>
                <FG label="Select members to remove">
                  <MultiSelect
                    value={selectedBound}
                    onChange={setSelectedBound}
                    options={
                      boundOpts.length
                        ? boundOpts
                        : [{ value: "__none__", label: "No members yet" }]
                    }
                    placeholder="Choose members to remove…"
                  />
                </FG>
                <div className="fb gap-8 pt-8">
                  <Btn
                    variant="d"
                    onClick={unbindSelected}
                    disabled={saving || !selectedBound.length}
                  >
                    {saving ? (
                      <span className="spin-icon" />
                    ) : (
                      "← Remove from Group"
                    )}
                  </Btn>
                </div>
              </>
            ) : (
              <DataTable
                columns={["Name"]}
                rows={boundUsers.map((u) => (
                  <tr key={u.user_id ?? u.id}>
                    <td className="fw-6">{displayName(u)}</td>
                  </tr>
                ))}
                emptyMsg="No members in this group"
              />
            )}
          </FormCard>
        </div>
      )}
      <div className="fb" style={{ marginTop: 24 }}>
        <Btn variant="g" onClick={onBack}>
          Back
        </Btn>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   MANAGE GROUP PERMISSIONS PAGE
═══════════════════════════════════════════════════════ */
function ManageGroupPermissionsPage({ group, toast, onBack }) {
  const { canUpdateAccessGroup } = usePermissions();

  const gid = group.group_id ?? group.id;
  const [allPerms, setAllPerms] = useState([]);
  const [assigned, setAssigned] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const [allRes, boundRes] = await Promise.all([
      apiFetch("GET", "/api/v1/amis/permission/all"),
      apiFetch("POST", "/api/v1/amis/group/bound-permissions", {
        group_id: gid,
      }),
    ]);
    const perms = Array.isArray(allRes)
      ? allRes
      : allRes?.permissions || allRes?.data || [];
    setAllPerms(perms);
    const boundArr = Array.isArray(boundRes)
      ? boundRes
      : boundRes?.permissions || boundRes?.data || [];
    setAssigned(new Set(boundArr.map((p) => p.code).filter(Boolean)));
    setLoading(false);
  }, [gid]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleSave() {
    if (!canUpdateAccessGroup) return;
    setSaving(true);
    setSaveStatus(null);
    const freshBound = await apiFetch(
      "POST",
      "/api/v1/amis/group/bound-permissions",
      { group_id: gid },
    );
    const boundArr = Array.isArray(freshBound)
      ? freshBound
      : freshBound?.permissions || freshBound?.data || [];
    const originalBound = new Map();
    for (const p of boundArr) originalBound.set(p.code, p);

    let ok = true;
    for (const p of allPerms) {
      const wasRecord = originalBound.get(p.code);
      const isAssigned = assigned.has(p.code);
      const pid = p.permission_id ?? p.id;
      if (isAssigned && !wasRecord) {
        const res = await GroupAPI.bindPermission(gid, pid, true);
        if (res === null) ok = false;
      } else if (!isAssigned && wasRecord) {
        const junctionId = wasRecord.group_permission_id ?? wasRecord.id ?? pid;
        const res = await GroupAPI.unbindPermission(junctionId);
        if (res === null) ok = false;
      }
    }

    setSaveStatus(ok ? "saved" : "error");
    toast?.(
      ok ? "Group permissions updated" : "Some permission changes failed",
      ok ? "success" : "danger",
    );
    await load();
    setSaving(false);
    setTimeout(() => setSaveStatus(null), 3500);
  }

  function togglePerm(code) {
    if (!canUpdateAccessGroup) return;
    setAssigned((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  function toggleModule(mod, grouped, allChecked) {
    if (!canUpdateAccessGroup) return;
    setAssigned((prev) => {
      const next = new Set(prev);
      for (const p of grouped[mod]) {
        if (allChecked) next.delete(p.code);
        else next.add(p.code);
      }
      return next;
    });
  }

  const grouped = {};
  for (const p of allPerms) {
    const mod = p.module || "OTHER";
    if (!grouped[mod]) grouped[mod] = [];
    grouped[mod].push(p);
  }
  const modules = Object.keys(grouped).sort();

  const visiblePerms = search.trim()
    ? allPerms.filter((p) =>
        `${p.name || ""} ${p.code || ""} ${p.module || ""}`
          .toLowerCase()
          .includes(search.toLowerCase()),
      )
    : allPerms;
  const allowedCount = allPerms.filter((p) => assigned.has(p.code)).length;

  return (
    <div className="page action-page" style={{ maxWidth: 1600 }}>
      <div className="mb-22">
        <div className="action-page-header" style={{ marginTop: 24 }}>
          <p className="action-page-subtitle">
            {canUpdateAccessGroup
              ? "Bind or update permissions for this group"
              : "View permissions for this group"}
          </p>
          <div className="action-page-subject">
            {group.name || group.group_name}
          </div>
        </div>
      </div>

      {!canUpdateAccessGroup && (
        <ReadOnlyBanner message="You have view-only access to group permissions." />
      )}

      {loading ? (
        <div className="empty" style={{ padding: "32px 0" }}>
          Loading permissions…
        </div>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 14,
              flexWrap: "wrap",
            }}
          >
            <div className="tsearch" style={{ flex: 1, minWidth: 180 }}>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search permissions…"
              />
            </div>
            <span
              style={{
                fontSize: 12,
                color: "var(--text3)",
                whiteSpace: "nowrap",
              }}
            >
              {allowedCount} / {allPerms.length} selected
            </span>
          </div>
          <SaveBanner status={saveStatus} />

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {modules.map((mod) => {
              const perms = grouped[mod].filter(
                (p) => visiblePerms.includes(p) || !search.trim(),
              );
              if (perms.length === 0) return null;
              const checkedCount = perms.filter((p) =>
                assigned.has(p.code),
              ).length;
              const allChecked = checkedCount === perms.length;
              const someChecked = checkedCount > 0 && !allChecked;
              return (
                <div
                  key={mod}
                  style={{
                    background: "var(--elevated)",
                    border: "1px solid var(--border)",
                    borderRadius: 10,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "11px 14px",
                      cursor: canUpdateAccessGroup ? "pointer" : "default",
                      userSelect: "none",
                      background: "var(--elevated)",
                      borderBottom: "1px solid var(--border)",
                    }}
                    onClick={() =>
                      canUpdateAccessGroup &&
                      toggleModule(mod, grouped, allChecked)
                    }
                  >
                    <div
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: 4,
                        flexShrink: 0,
                        border: allChecked
                          ? "none"
                          : someChecked
                            ? "1.5px solid var(--flame)"
                            : "1.5px solid var(--border)",
                        background: allChecked
                          ? "var(--flame)"
                          : someChecked
                            ? "rgba(255,79,23,0.18)"
                            : "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: canUpdateAccessGroup ? "pointer" : "default",
                        transition: "all 0.12s",
                        opacity: canUpdateAccessGroup ? 1 : 0.6,
                      }}
                    >
                      {allChecked && (
                        <svg
                          width="9"
                          height="9"
                          viewBox="0 0 10 8"
                          fill="none"
                        >
                          <path
                            d="M1 4l3 3 5-6"
                            stroke="#fff"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                      {someChecked && !allChecked && (
                        <div
                          style={{
                            width: 8,
                            height: 2,
                            background: "var(--flame)",
                            borderRadius: 2,
                          }}
                        />
                      )}
                    </div>
                    <span
                      style={{
                        flex: 1,
                        fontSize: 12,
                        fontWeight: 700,
                        color: "var(--text1)",
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                      }}
                    >
                      {mod.replace(/_/g, " ")}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        color:
                          checkedCount > 0 ? "var(--flame)" : "var(--text3)",
                        fontWeight: 600,
                        background:
                          checkedCount > 0
                            ? "rgba(255,79,23,0.10)"
                            : "var(--surface)",
                        border: `1px solid ${checkedCount > 0 ? "rgba(255,79,23,0.25)" : "var(--border)"}`,
                        borderRadius: 6,
                        padding: "2px 8px",
                        marginRight: 4,
                      }}
                    >
                      {checkedCount}/{perms.length}
                    </span>
                  </div>
                  <div>
                    {perms.map((p, idx) => {
                      const checked = assigned.has(p.code);
                      return (
                        <div
                          key={p.code}
                          onClick={() => togglePerm(p.code)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            padding: "10px 14px 10px 44px",
                            cursor: canUpdateAccessGroup
                              ? "pointer"
                              : "default",
                            borderBottom:
                              idx < perms.length - 1
                                ? "1px solid var(--border)"
                                : "none",
                            background: checked
                              ? "rgba(59,130,246,0.04)"
                              : "transparent",
                            transition: "background 0.1s",
                          }}
                        >
                          <div
                            style={{
                              width: 15,
                              height: 15,
                              borderRadius: 4,
                              flexShrink: 0,
                              border: checked
                                ? "none"
                                : "1.5px solid var(--border)",
                              background: checked
                                ? "var(--blue)"
                                : "transparent",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.12s",
                              opacity: canUpdateAccessGroup ? 1 : 0.6,
                            }}
                          >
                            {checked && (
                              <svg
                                width="9"
                                height="9"
                                viewBox="0 0 10 8"
                                fill="none"
                              >
                                <path
                                  d="M1 4l3 3 5-6"
                                  stroke="#fff"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: "var(--text1)",
                                lineHeight: 1.35,
                              }}
                            >
                              {p.name || p.permission_name || p.code}
                            </div>
                            <div
                              style={{
                                fontSize: 10,
                                color: "var(--text3)",
                                marginTop: 2,
                                fontFamily: "monospace",
                              }}
                            >
                              {p.code}
                            </div>
                          </div>
                          {p.action && (
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: "0.06em",
                                padding: "2px 7px",
                                borderRadius: 5,
                                background: actionColor(p.action).bg,
                                color: actionColor(p.action).fg,
                                flexShrink: 0,
                              }}
                            >
                              {p.action}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {canUpdateAccessGroup && (
            <div
              style={{
                position: "sticky",
                bottom: 0,
                paddingTop: 14,
                paddingBottom: 4,
                background: "var(--void)",
                zIndex: 10,
                display: "flex",
                flexDirection: "column",
                gap: 8,
                alignItems: "flex-end",
              }}
            >
              <SaveBanner status={saveStatus} />
              <Btn variant="p" onClick={handleSave} disabled={saving}>
                {saving ? <span className="spin-icon" /> : "Update Permissions"}
              </Btn>
            </div>
          )}

          <div className="fb" style={{ marginTop: 24 }}>
            <Btn variant="g" onClick={onBack}>
              Back
            </Btn>
          </div>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   ROOT
═══════════════════════════════════════════════════════ */
const TABS = [
  { id: "users", label: " Users" },
  { id: "groups", label: " Groups & Permissions" },
];

export default function MaintenancePage({ showToast: externalToast }) {
  const [activeTab, setActiveTab] = useState("users");
  const [page, setPage] = useState(null);
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [toasts, setToasts] = useState([]);

  const permissions = usePermissions();

  function toast(msg, type = "info") {
    if (externalToast) externalToast(msg, type);
    const id = Date.now();
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3500);
  }

  useEffect(() => {
    (async () => {
      const [u, b, w] = await Promise.all([
        UserAPI.getAll(),
        BranchAPI.getAll(),
        WarehouseAPI.getAll(),
      ]);
      setUsers(u || []);
      setBranches(b || []);
      setWarehouses(w || []);
    })();
  }, []);

  function navigate(view, data = null) {
    const guards = {
      addUser: permissions.canCreateUsers,
      editUser: permissions.canUpdateUsers || permissions.canViewUsers,
      deleteUser: permissions.canDeleteUsers,
      updatePassword: permissions.canUpdateUsers,
      addGroup: permissions.canCreateGroups,
      editGroup: permissions.canUpdateGroups || permissions.canViewGroups,
      manageGroupMembers:
        permissions.canUpdateUserGroup || permissions.canViewGroups,
      manageGroupPermissions:
        permissions.canUpdateAccessGroup || permissions.canViewPermissions,
    };

    if (Object.prototype.hasOwnProperty.call(guards, view) && !guards[view]) {
      toast("You don't have permission to perform this action.", "danger");
      return;
    }

    setPage({ view, data });
  }

  function goBack() {
    setPage(null);
  }

  if (permissions.ready && !permissions.canSeePage("Maintenance")) return null;

  if (page) {
    const { view, data } = page;

    if (view === "addUser")
      return (
        <AddUserPage
          branches={branches}
          warehouses={warehouses}
          users={users}
          onSave={(item) => {
            setUsers((a) => [...a, item]);
            toast("User created", "success");
            goBack();
          }}
          onBack={goBack}
        />
      );

    if (view === "editUser")
      return (
        <EditUserPage
          user={data}
          branches={branches}
          warehouses={warehouses}
          onSave={(item) => {
            setUsers((a) =>
              a.map((x) =>
                resolveUserId(x) === resolveUserId(item) ? item : x,
              ),
            );
            toast("User updated", "success");
          }}
          onBack={goBack}
        />
      );

    if (view === "deleteUser")
      return (
        <DeleteUserPage
          user={data}
          onConfirm={(id) => {
            setUsers((a) => a.filter((x) => resolveUserId(x) !== id));
            toast("User deleted", "warning");
            goBack();
          }}
          onBack={goBack}
        />
      );

    if (view === "updatePassword")
      return (
        <UpdatePasswordPage
          user={data}
          onSave={() => {
            toast("Password updated", "success");
            goBack();
          }}
          onBack={goBack}
        />
      );

    if (view === "addGroup")
      return (
        <AddGroupPage
          onSave={() => {
            toast("Group created", "success");
            goBack();
          }}
          onBack={goBack}
        />
      );

    if (view === "editGroup")
      return (
        <EditGroupPage
          group={data}
          onSave={() => {
            toast("Group updated", "success");
            goBack();
          }}
          onBack={goBack}
        />
      );

    if (view === "manageGroupMembers")
      return (
        <ManageGroupMembersPage group={data} toast={toast} onBack={goBack} />
      );

    if (view === "manageGroupPermissions")
      return (
        <ManageGroupPermissionsPage
          group={data}
          toast={toast}
          onBack={goBack}
        />
      );
  }

  return (
    <div className="page">
      <div className="tabs mb-22" style={{ marginBottom: 10 }}>
        {TABS.map((tab) => (
          <div
            key={tab.id}
            className={`tab${activeTab === tab.id ? " on" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </div>
        ))}
      </div>
      {activeTab === "users" && (
        <UsersSection
          users={users}
          branches={branches}
          warehouses={warehouses}
          onNavigate={navigate}
          toast={toast}
        />
      )}
      {activeTab === "groups" && (
        <GroupsSection onNavigate={navigate} toast={toast} />
      )}
      <Toast toasts={toasts} />
    </div>
  );
}

export function useMaintenanceTheme() {
  const [theme, setTheme] = useState(
    () => document.documentElement.getAttribute("data-theme") || "dark",
  );
  function toggleTheme() {
    const next = theme === "dark" ? "light" : "dark";
    document.documentElement.setAttribute("data-theme", next);
    document.body.setAttribute("data-theme", next);
    setTheme(next);
  }
  useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() =>
      setTheme(el.getAttribute("data-theme") || "dark"),
    );
    obs.observe(el, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);
  return { theme, toggleTheme };
}
